"use client";

import React, { useId, useMemo, memo } from 'react';
import { scoreTierColors } from '@/lib/rating-tiers';

interface RadarChartProps {
  stock: any;
  isLoading?: boolean;
  values?: number[];
  labels?: string[];
  strategyScore?: number;
  scaleMax?: number;
  strategyweights?: string | null;
  overrideColor?: string;
  onAxisClick?: (index: number, label: string) => void;
  /** Index of the axis whose LINE + label to highlight (0-based). */
  highlightAxisIndex?: number | null;
  /** Index(es) of the sector wedge(s) to fill with `highlightColor` (0-based).
   *  Accepts a single number or an array for multi-factor strategies. */
  highlightSectorIndex?: number | number[] | null;
  /** Color used for the highlight (axis line, label, or sector fill). */
  highlightColor?: string;
  /** Rendered SVG width in px (height follows viewBox aspect ratio; default 280). */
  size?: number;
  /** SVG viewBox — default includes label padding for full-size layouts. */
  viewBox?: string;
  /** Short transitions for strategy switching (sidebar interactions). */
  snappyTransitions?: boolean;
  /** Skip bloom filters — crisper fill for compact Key Ratings cells. */
  disableGlow?: boolean;
  /** Override axis label font size (SVG units). Default 15 / 14.5 by axis count. */
  labelFontSize?: number;
  /** Hover a factor sector / axis label — index null on leave. */
  onSectorHover?: (index: number | null, label?: string) => void;
  /**
   * Print each axis score under its label in the tier color. The top label
   * moves out to make room; needs a viewBox with ~16 units of extra room at
   * the top and bottom.
   */
  showAxisValues?: boolean;
  /**
   * Terminal-board labels: small caps with tracking, no glow. Factors the
   * strategy uses read white, the rest dim; with showAxisValues only those
   * factors get their score.
   */
  terminal?: boolean;
  /** Tint the background wedge of each highlighted sector (default true).
   *  Off: only the shape takes the colour (GEAR+ lights all five sectors,
   *  and a fully tinted disc drowned out the score). */
  sectorBackdrop?: boolean;
}

const DEFAULT_RADAR_VIEWBOX = '0 -50 320 370';

function parseViewBox(viewBox: string) {
  const [minX, minY, width, height] = viewBox.trim().split(/\s+/).map(Number);
  return { minX, minY, width, height };
}

/** Score ring radii — matches grid stroke circles (100 → 0). */
const RADAR_LEVEL_OUTER = [85, 70, 54, 38, 23, 8];
const RADAR_LEVEL_INNER = [70, 54, 38, 23, 8, 0];
const RADAR_BAND_LIGHT = 'rgba(255, 255, 255, 0.085)';
const RADAR_BAND_DARK = 'rgba(255, 255, 255, 0.028)';

function createWedgeRingPath(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startAngle: number,
  endAngle: number,
): string {
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  const ox1 = cx + outerR * Math.cos(startAngle);
  const oy1 = cy + outerR * Math.sin(startAngle);
  const ox2 = cx + outerR * Math.cos(endAngle);
  const oy2 = cy + outerR * Math.sin(endAngle);

  if (innerR <= 0) {
    return `M ${cx} ${cy} L ${ox1} ${oy1} A ${outerR} ${outerR} 0 ${largeArc} 1 ${ox2} ${oy2} Z`;
  }

  const ix1 = cx + innerR * Math.cos(endAngle);
  const iy1 = cy + innerR * Math.sin(endAngle);
  const ix2 = cx + innerR * Math.cos(startAngle);
  const iy2 = cy + innerR * Math.sin(startAngle);

  return [
    `M ${ox1} ${oy1}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${ox2} ${oy2}`,
    `L ${ix1} ${iy1}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${ix2} ${iy2}`,
    'Z',
  ].join(' ');
}

// Функция для парсинга весов из строки strategyweights
// Формат: "MetricName (weight%), MetricName (weight%), ..."
function parseWeights(strategyweights: string | null | undefined, labels: string[]): (string | null)[] {
  if (!strategyweights) {
    return labels.map(() => null);
  }
  
  // Извлекаем все пары (название, вес) из строки
  const weightMatches = strategyweights.match(/([^,()]+)\s*\((\d+)%\)/g) || [];
  const weightMap: Record<string, string> = {};
  
  weightMatches.forEach(match => {
    const parts = match.match(/([^()]+)\s*\((\d+)%\)/);
    if (parts) {
      const name = parts[1].trim().toUpperCase();
      const weight = parts[2];
      weightMap[name] = weight + '%';
    }
  });
  
  // Сопоставляем веса с лейблами
  return labels.map(label => {
    const labelUpper = label.toUpperCase();
    
    // Прямое совпадение
    if (weightMap[labelUpper]) return weightMap[labelUpper];
    
    // Частичное совпадение
    for (const [name, weight] of Object.entries(weightMap)) {
      // Проверяем содержит ли имя метрики наш лейбл или наоборот
      if (name.includes(labelUpper) || labelUpper.includes(name.replace(/\s+/g, ' ').trim())) {
        return weight;
      }
      // Специальные маппинги
      const mappings: Record<string, string[]> = {
        'PE FWD': ['P/E', 'PE', 'P/E FWD', 'P/E, FWD'],
        'PE 3Y': ['P/E FY3', 'P/E FY+3', 'PE 3Y', 'P/E 3Y'],
        'P/BV': ['P/BV', 'PBV'],
        'EV/EBITDA': ['EV/EBITDA', 'EVEBITDA'],
        'EV/EBIT': ['EV/EBIT', 'EVEBIT'],
        'FCF YIELD': ['FCF YIELD', 'FCF YLD'],
        'FCF YLD': ['FCF YIELD', 'FCF YLD'],
        'DIV YIELD': ['DIV YIELD', 'DIVIDEND'],
        'GROWTH': ['GROWTH', 'PURE GROWTH'],
        'QUALITY': ['QUALITY', 'PURE QUALITY'],
        'VALUE': ['VALUE', 'PURE VALUE'],
        'MOMENTUM': ['MOMENTUM', 'PURE MOMENTUM'],
        'ANTIFRAGILITY': ['ANTIFRAGILITY', 'PURE ANTIFRAGILITY', 'ANTI'],
        'REV 3Y': ['REV 3Y', 'REVENUE 3Y', 'HIST GROWTH', 'REV HIST 3Y'],
        'REV FWD 1Y': ['REV FWD 1Y', 'REVENUE FWD 1Y', 'EXP GROWTH 1Y', 'REV EXP 1Y'],
        'REV FWD 3Y': ['REV FWD 3Y', 'REVENUE FWD 3Y', 'EXP GROWTH 3Y', 'REV EXP 3Y'],
        'REV FWD 5Y': ['REV FWD 5Y', 'REVENUE FWD 5Y', 'EXP GROWTH 5Y'],
        'EPS FY1': ['EPS FY1', 'EPS GROWTH FY1', 'EPS Y+1'],
        'EPS FY2': ['EPS FY2', 'EPS GROWTH FY2', 'EPS Y+2'],
        'EPS FY3': ['EPS FY3', 'EPS GROWTH FY3', 'EPS Y+3'],
        'EBIT MGN': ['EBIT', 'EBIT MGN', 'EBIT MARGIN'],
        'NET INC MGN': ['NET INC', 'NET INCOME', 'NI MARGIN'],
        'FCF MGN': ['FCF MGN', 'FCF MARGIN'],
        'ROE': ['ROE'],
        'ROA': ['ROA'],
        'ROCE': ['ROCE'],
        'EARN Q': ['EARN Q', 'EARNINGS QUALITY', 'EARNINGS STABILITY'],
        'DEBT': ['DEBT', 'NET DEBT'],
        '1M': ['1M', '1 MONTH'],
        '3M': ['3M', '3 MONTH'],
        'YTD': ['YTD'],
        '52W HIGH': ['52W', '52 WEEK'],
        '1Y': ['1Y', '1 YEAR'],
        '3Y': ['3Y', '3 YEAR'],
        '5Y': ['5Y', '5 YEAR'],
        'BETA': ['BETA'],
        'VOL': ['VOL', 'VOLATILITY'],
      };
      
      const labelMappings = mappings[labelUpper] || [];
      for (const mapping of labelMappings) {
        if (name.includes(mapping) || mapping.includes(name)) {
          return weight;
        }
      }
    }
    
    return null;
  });
}

function RadarChart({
  stock, 
  isLoading = false,
  values,
  labels,
  strategyScore,
  scaleMax = 100,
  strategyweights,
  overrideColor,
  onAxisClick,
  highlightAxisIndex = null,
  highlightSectorIndex: highlightSectorIndexProp = null,
  highlightColor,
  size = 280,
  viewBox = DEFAULT_RADAR_VIEWBOX,
  snappyTransitions = false,
  disableGlow = false,
  labelFontSize,
  onSectorHover,
  showAxisValues = false,
  terminal = false,
  sectorBackdrop = true,
}: RadarChartProps) {
  const highlightSectors: number[] = highlightSectorIndexProp == null ? [] : Array.isArray(highlightSectorIndexProp) ? highlightSectorIndexProp : [highlightSectorIndexProp];
  const hasHighlight = highlightSectors.length > 0;
  const hoverEnabled = typeof onSectorHover === 'function';

  // Если переданы values и labels - используем их, иначе дефолтные 5 факторов
  const strategyValues = useMemo(() => {
    if (values && values.length > 0) {
      return values;
    }
    return [
      stock.PureGrowth || 0,
      stock.PureQuality || 0,
      stock.PureValue || 0,
      stock.PureMomentum || 0,
      stock.PureAntifragility || 0
    ];
  }, [values, stock]);
  
  const strategyLabels = useMemo(() => {
    if (labels && labels.length > 0) {
      return labels;
    }
    return ['GROWTH', 'QUALITY', 'VALUE', 'MOMENTUM', 'RISK SHIELD'];
  }, [labels]);
  
  // Score для цвета - используем переданный или GEAR5FScore
  const scoreForColor = strategyScore !== undefined ? strategyScore : (stock.GEAR5FScore || 0);
  
  const centerX = 160;
  const centerY = 160;
  const radius = 85; // Радиус диаграммы
  const numPoints = strategyValues.length;

  // Закрытый контур по числу осей (5 для GEAR+, 7 для расширенных стратегий).
  // Раньше 5-осевой путь искусственно расширялся до 7 точек с интерполяциями
  // между Growth↔Quality и RiskShield↔Growth — это давало лишний "горб"
  // между RiskShield и Growth, особенно когда значение низкое.
  const points = calculateRadarPoints(strategyValues, centerX, centerY, radius, scaleMax, numPoints);
  const smoothPath = createSmoothPath(points, centerX, centerY);
  
  // Рассчитываем координаты для подписей динамически
  // Для 5 факторов - используем разные отступы, для 7 - равномерные с большим отступом
  const labelDistances = useMemo(() => {
    if (numPoints === 5) {
      // Специальные отступы для 5 факторов (первый ближе).
      // With axis scores the top label moves out so its score fits below it.
      return [radius + (showAxisValues ? 40 : 22), radius + 38, radius + 38, radius + 38, radius + 38];
    }
    // Для 7 факторов - увеличенные равномерные отступы
    return Array(numPoints).fill(radius + 34);
  }, [numPoints, radius, showAxisValues]);
  
  const labelPoints = labelDistances.map((distance, index) => {
    const angle = (index * 2 * Math.PI) / numPoints - Math.PI / 2; // -90 градусов чтобы первый лейбл был сверху
    return {
      x: centerX + distance * Math.cos(angle),
      y: centerY + distance * Math.sin(angle)
    };
  });

  // Оси рисуем по numPoints (5 или 7), чтобы совпадали с подписями; path при этом всегда из 7 точек
  const axisPoints = useMemo(() => {
    return Array.from({ length: numPoints }, (_, index) => {
      const angle = (index * 2 * Math.PI) / numPoints - Math.PI / 2;
      return {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle)
      };
    });
  }, [numPoints, centerX, centerY, radius]);
  
  const hasValidScore = scoreForColor != null && scoreForColor !== 0 && isFinite(scoreForColor);

  const gearGradient = overrideColor
    ? (() => {
        const hex = overrideColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const br = (v: number, f: number) => Math.min(255, v + f);
        const dk = (v: number, f: number) => Math.max(0, v - f);
        return { start: `rgb(${br(r,15)},${br(g,15)},${br(b,15)})`, middle: overrideColor, end: `rgb(${dk(r,15)},${dk(g,15)},${dk(b,15)})` };
      })()
    : (hasValidScore ? createGearGradient(scoreForColor) : createGearGradient(null));

  const radarAccent = overrideColor || (hasValidScore ? getStrategyScoreColor(scoreForColor) : '#6b7280');

  // useId matches between server and client render (a module counter did not).
  const uid = `rc${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const { width: vbWidth, height: vbHeight } = useMemo(() => parseViewBox(viewBox), [viewBox]);
  const renderHeight = Math.round(size * vbHeight / vbWidth);
  const motionTransition = snappyTransitions ? '0.07s ease' : '0.3s ease';
  const pathTransition = snappyTransitions ? '0.1s ease' : '0.4s cubic-bezier(0.4, 0, 0.2, 1)';
  
  return (
    <svg
      width={size}
      height={renderHeight}
      viewBox={viewBox}
      // Shrinks with a narrow card instead of overflowing it.
      style={{ display: "block", margin: 0, maxWidth: "100%", height: "auto" }}
      onMouseLeave={hoverEnabled ? () => onSectorHover(null) : undefined}
    >
      <defs>
        <linearGradient id={`dataGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={gearGradient.start} stopOpacity="0.7"/>
          <stop offset="30%" stopColor={gearGradient.middle} stopOpacity="0.6"/>
          <stop offset="70%" stopColor={gearGradient.end} stopOpacity="0.5"/>
          <stop offset="100%" stopColor={gearGradient.end} stopOpacity="0.4"/>
        </linearGradient>
        <linearGradient id={`strokeGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={gearGradient.start}/>
          <stop offset="50%" stopColor={gearGradient.middle}/>
          <stop offset="100%" stopColor={gearGradient.end}/>
        </linearGradient>
        <filter id={`glow-${uid}`}>
          <feGaussianBlur stdDeviation="6" result="coloredBlur"/>
          <feMerge> 
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <filter id={`softGlow-${uid}`}>
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge> 
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {/* Alternating light/dark bands per wedge — makes score levels readable */}
      {Array.from({ length: numPoints }, (_, wedgeIndex) => {
        const angleStep = (2 * Math.PI) / numPoints;
        const startAngle = (wedgeIndex - 0.5) * angleStep - Math.PI / 2;
        const endAngle = (wedgeIndex + 0.5) * angleStep - Math.PI / 2;
        return RADAR_LEVEL_OUTER.map((outerR, bandIndex) => (
          <path
            key={`wedge-${wedgeIndex}-band-${bandIndex}`}
            d={createWedgeRingPath(
              centerX,
              centerY,
              RADAR_LEVEL_INNER[bandIndex],
              outerR,
              startAngle,
              endAngle,
            )}
            fill={bandIndex % 2 === 0 ? RADAR_BAND_LIGHT : RADAR_BAND_DARK}
          />
        ));
      })}
      
      {/* Сетка - концентрические круги с новой шкалой (-10 до 100) */}
      {/* Внешний круг (100) */}
      <circle cx={centerX} cy={centerY} r="85" fill="none" stroke="#374151" strokeWidth="1" opacity="0.4"/>
      {/* 80 */}
      <circle cx={centerX} cy={centerY} r="70" fill="none" stroke="#374151" strokeWidth="1" opacity="0.3"/>
      {/* 60 */}
      <circle cx={centerX} cy={centerY} r="54" fill="none" stroke="#374151" strokeWidth="1" opacity="0.3"/>
      {/* 40 */}
      <circle cx={centerX} cy={centerY} r="38" fill="none" stroke="#374151" strokeWidth="1" opacity="0.3"/>
      {/* 20 */}
      <circle cx={centerX} cy={centerY} r="23" fill="none" stroke="#374151" strokeWidth="1" opacity="0.3"/>
      {/* 0 */}
      <circle cx={centerX} cy={centerY} r="8" fill="none" stroke="#374151" strokeWidth="1" opacity="0.3"/>
      {/* -10 (центр) */}
      <circle cx={centerX} cy={centerY} r="2" fill="#374151" opacity="0.6"/>
      
      {/* Подсветка сектора (клин между двумя соседними осями) — используется
          в Charts-сайдбаре чтобы выделить фактор текущей подсекции, оставив
          сам полигон данных нейтральным. */}
      {hasHighlight && sectorBackdrop && highlightSectors.map(i => {
        const angleStep = (2 * Math.PI) / numPoints;
        const startAngle = (i - 0.5) * angleStep - Math.PI / 2;
        const endAngle   = (i + 0.5) * angleStep - Math.PI / 2;
        const sx = centerX + radius * Math.cos(startAngle);
        const sy = centerY + radius * Math.sin(startAngle);
        const ex = centerX + radius * Math.cos(endAngle);
        const ey = centerY + radius * Math.sin(endAngle);
        const largeArc = angleStep > Math.PI ? 1 : 0;
        const sectorPath = `M ${centerX} ${centerY} L ${sx} ${sy} A ${radius} ${radius} 0 ${largeArc} 1 ${ex} ${ey} Z`;
        const fillColor = highlightColor || radarAccent;
        return (
          <path
            key={`sector-bg-${i}`}
            d={sectorPath}
            fill={fillColor}
            opacity="0.18"
            style={{ transition: `opacity ${motionTransition}, fill ${motionTransition}` }}
          />
        );
      })}
      
      {/* Оси - радиальные линии (по numPoints, чтобы совпадали с подписями) */}
      {axisPoints.map((point, index) => {
        const isHighlighted = highlightAxisIndex === index;
        const highlightStroke = highlightColor || radarAccent;
        return (
          <line
            key={index}
            x1={centerX}
            y1={centerY}
            x2={point.x}
            y2={point.y}
            stroke={isHighlighted ? highlightStroke : "#4b5563"}
            strokeWidth={isHighlighted ? "2" : "1"}
            opacity={isHighlighted ? "0.95" : "0.6"}
            style={{ transition: `stroke ${motionTransition}, opacity ${motionTransition}, stroke-width ${motionTransition}` }}
          />
        );
      })}
      
      {/* Подписи */}
      {labelPoints.map((point, index) => {
        const rawLabel = strategyLabels[index] || '';
        const label = terminal ? rawLabel.toUpperCase() : rawLabel;
        const words = label.trim().split(/\s+/).filter(Boolean);
        const isMultiLine = words.length > 1;
        const fontSize = labelFontSize ?? (numPoints > 5 ? 14.5 : 15);
        const lineStep = fontSize * 0.92;
        const isClickable = !!onAxisClick;
        const isInteractive = isClickable || hoverEnabled;
        const isHighlighted = highlightAxisIndex === index || highlightSectors.includes(index);
        const labelFill = terminal
          ? (isHighlighted || !hasHighlight ? '#ffffff' : 'rgba(255,255,255,0.32)')
          : isHighlighted ? (highlightColor || radarAccent) : (hasHighlight ? 'rgba(255,255,255,0.3)' : '#ffffff');
        const yBase = isMultiLine
          ? point.y - ((words.length - 1) * lineStep) / 2
          : point.y + 4;

        return (
          <text 
            key={index}
            x={point.x} 
            y={yBase} 
            textAnchor="middle" 
            onClick={isClickable ? () => onAxisClick(index, rawLabel) : undefined}
            style={{ 
              fontSize, 
              fill: labelFill, 
              fontFamily: terminal ? 'var(--font-inter), sans-serif' : "Arial",
              fontWeight: terminal ? "600" : isHighlighted ? "700" : "600",
              textShadow: isHighlighted && !terminal ? `0 0 10px ${labelFill}` : 'none',
              letterSpacing: terminal ? "0.08em" : "0.5px",
              cursor: isInteractive ? 'pointer' : undefined,
              pointerEvents: isInteractive ? 'auto' : undefined,
              transition: `fill ${motionTransition}, font-weight ${motionTransition}, opacity ${motionTransition}`,
            }}
            opacity={1}
            onMouseEnter={hoverEnabled ? () => onSectorHover(index, rawLabel) : undefined}
          >
            {isMultiLine ? (
              words.map((word, wi) => (
                <tspan key={word} x={point.x} dy={wi === 0 ? 0 : lineStep}>
                  {word}
                </tspan>
              ))
            ) : (
              label
            )}
          </text>
        );
      })}

      {/* Axis scores: below every label */}
      {showAxisValues && labelPoints.map((point, index) => {
        const raw = Number(strategyValues[index]);
        if (!Number.isFinite(raw) || raw <= 0) return null;
        const label = strategyLabels[index] || '';
        const lineCount = Math.max(1, label.trim().split(/\s+/).filter(Boolean).length);
        const fontSize = labelFontSize ?? (numPoints > 5 ? 14.5 : 15);
        const lineStep = fontSize * 0.92;
        const firstLineY = lineCount > 1
          ? point.y - ((lineCount - 1) * lineStep) / 2
          : point.y + 4;
        const valueY = firstLineY + (lineCount - 1) * lineStep + fontSize * 1.32;
        const isHighlighted = highlightAxisIndex === index || highlightSectors.includes(index);
        const dimmed = hasHighlight && !isHighlighted;
        if (terminal && dimmed) return null;
        return (
          <text
            key={`v-${index}`}
            x={point.x}
            y={valueY}
            textAnchor="middle"
            opacity={dimmed ? 0.35 : 1}
            style={{
              fontSize: terminal ? fontSize * 1.1 : fontSize * 1.05,
              fill: scoreTierColors(raw).text,
              fontFamily: terminal ? 'var(--font-inter), sans-serif' : 'var(--font-bastion)',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              pointerEvents: 'none',
              transition: `opacity ${motionTransition}`,
            }}
          >
            {Math.round(raw)}
          </text>
        );
      })}
      
      {/* Анимированная стрелка радара при загрузке */}
      {isLoading && (
        <>
          {/* Вращающаяся стрелка */}
          <g className="radar-sweep">
            <line
              x1={centerX}
              y1={centerY}
              x2={centerX}
              y2={centerY - 85}
              stroke={radarAccent}
              strokeWidth="3"
              opacity="0.9"
              filter={`url(#glow-${uid})`}
            />
            <circle
              cx={centerX}
              cy={centerY}
              r="4"
              fill={radarAccent}
              opacity="0.9"
            />
          </g>
          
          {/* Пульсирующие круги */}
          <circle
            cx={centerX}
            cy={centerY}
            r="85"
            fill="none"
            stroke={radarAccent}
            strokeWidth="2"
            opacity="0.3"
            className="radar-pulse"
          />
          <circle
            cx={centerX}
            cy={centerY}
            r="60"
            fill="none"
            stroke={radarAccent}
            strokeWidth="1"
            opacity="0.2"
            className="radar-pulse"
            style={{ animationDelay: '0.5s' }}
          />
          <circle
            cx={centerX}
            cy={centerY}
            r="35"
            fill="none"
            stroke={radarAccent}
            strokeWidth="1"
            opacity="0.1"
            className="radar-pulse"
            style={{ animationDelay: '1s' }}
          />
        </>
      )}
      
      {/* Данные радара */}
      {!isLoading && (
        <g>
          <path
            d={smoothPath}
            fill={hasHighlight ? 'rgba(255,255,255,0.06)' : `url(#dataGrad-${uid})`}
            stroke={hasHighlight ? 'rgba(255,255,255,0.15)' : `url(#strokeGrad-${uid})`}
            strokeWidth="2.5"
            filter={hasHighlight || disableGlow ? undefined : `url(#glow-${uid})`}
            opacity={hasHighlight ? 1 : 0.65}
            style={{
              transition: pathTransition,
              cursor: 'pointer'
            }}
            className="hover:opacity-100 hover:drop-shadow-lg"
          />
          
          {/* Внутренняя подсветка для объема */}
          {!hasHighlight && !disableGlow && (
            <path
              d={smoothPath}
              fill="none"
              stroke={gearGradient.start}
              strokeWidth="1.5"
              opacity={0.7}
              filter={`url(#softGlow-${uid})`}
              style={{
                transition: `opacity ${motionTransition}`
              }}
              className="group-hover:opacity-90"
            />
          )}

          {/* Цветной "срез" полигона данных, обрезанный по wedge сектора —
              часть кривой, попадающая в выбранный сектор, отрисовывается
              поверх нейтральной заливки в цвете текущего фактора. */}
          {hasHighlight && highlightSectors.map(i => {
            const angleStep = (2 * Math.PI) / numPoints;
            const startAngle = (i - 0.5) * angleStep - Math.PI / 2;
            const endAngle   = (i + 0.5) * angleStep - Math.PI / 2;
            const sx = centerX + radius * Math.cos(startAngle);
            const sy = centerY + radius * Math.sin(startAngle);
            const ex = centerX + radius * Math.cos(endAngle);
            const ey = centerY + radius * Math.sin(endAngle);
            const largeArc = angleStep > Math.PI ? 1 : 0;
            const wedgePath = `M ${centerX} ${centerY} L ${sx} ${sy} A ${radius} ${radius} 0 ${largeArc} 1 ${ex} ${ey} Z`;
            const colorFill = highlightColor || radarAccent;
            const clipId = `sectorClip-${uid}-${i}`;
            return (
              <React.Fragment key={`slice-${i}`}>
                <defs>
                  <clipPath id={clipId}>
                    <path d={wedgePath} />
                  </clipPath>
                </defs>
                <g clipPath={`url(#${clipId})`}>
                  <path
                    d={smoothPath}
                    fill={colorFill}
                    opacity="0.75"
                    style={{ transition: `opacity ${motionTransition}, fill ${motionTransition}` }}
                  />
                  <path
                    d={smoothPath}
                    fill="none"
                    stroke={colorFill}
                    strokeWidth="2"
                    opacity="0.95"
                    style={{ transition: `stroke ${motionTransition}, opacity ${motionTransition}` }}
                  />
                </g>
              </React.Fragment>
            );
          })}
        </g>
      )}

      {/* Invisible sector hit targets for hover (above data, below labels in paint order —
          labels stay interactive via their own pointer events). */}
      {hoverEnabled && !isLoading && Array.from({ length: numPoints }, (_, i) => {
        const angleStep = (2 * Math.PI) / numPoints;
        const startAngle = (i - 0.5) * angleStep - Math.PI / 2;
        const endAngle = (i + 0.5) * angleStep - Math.PI / 2;
        const sx = centerX + radius * Math.cos(startAngle);
        const sy = centerY + radius * Math.sin(startAngle);
        const ex = centerX + radius * Math.cos(endAngle);
        const ey = centerY + radius * Math.sin(endAngle);
        const largeArc = angleStep > Math.PI ? 1 : 0;
        const sectorPath = `M ${centerX} ${centerY} L ${sx} ${sy} A ${radius} ${radius} 0 ${largeArc} 1 ${ex} ${ey} Z`;
        const label = strategyLabels[i] || '';
        return (
          <path
            key={`sector-hit-${i}`}
            d={sectorPath}
            fill="transparent"
            stroke="none"
            style={{ cursor: 'pointer', pointerEvents: 'auto' }}
            onMouseEnter={() => onSectorHover(i, label)}
          />
        );
      })}
    </svg>
  );
}

export default memo(RadarChart);

function calculateRadarPoints(
  values: number[],
  centerX: number,
  centerY: number,
  radius: number,
  scaleMax: number = 100,
  numAxes?: number
) {
  const axes = numAxes ?? values.length;
  const angleStep = (2 * Math.PI) / axes;

  return values.map((value, index) => {
    const minValue = scaleMax === 10 ? 0 : -10;
    const maxValue = scaleMax;
    const normalizedValue = Math.max(0, Math.min(1, (value - minValue) / (maxValue - minValue)));
    const pointRadius = normalizedValue * radius;

    const angle = (index * angleStep) - (Math.PI / 2);
    const x = centerX + (pointRadius * Math.cos(angle));
    const y = centerY + (pointRadius * Math.sin(angle));
    return { x, y };
  });
}

function createSmoothPath(points: {x: number, y: number}[], centerX: number, centerY: number) {
  if (points.length < 3) return '';
  
  const first = points[0];
  const second = points[1];
  const last = points[points.length - 1];
  const secondLast = points[points.length - 2];
  
  let path = `M ${first.x} ${first.y}`;
  
  for (let i = 0; i < points.length; i++) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    const prev = points[(i - 1 + points.length) % points.length];
    
    const cp1x = current.x + (next.x - prev.x) * 0.1;
    const cp1y = current.y + (next.y - prev.y) * 0.1;
    const cp2x = next.x - (points[(i + 2) % points.length].x - current.x) * 0.1;
    const cp2y = next.y - (points[(i + 2) % points.length].y - current.y) * 0.1;
    
    path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`;
  }
  
  return path + ' Z';
}

// Заливки рейтингов: ≥70 (A-, A, A+) #16a34a, ≥34 (B-, B, B+) #ca8a04, <34 (C-, C, C+) #dc2626
function getStrategyScoreColor(scoreValue: number | null | undefined): string {
  if (scoreValue == null || !isFinite(scoreValue)) return '#dc2626';
  const s = Math.max(0, Math.min(100, Number(scoreValue)));
  if (s >= 70) return '#16a34a';
  if (s >= 34) return '#ca8a04';
  return '#dc2626';
}

function createGearGradient(gearScore: number | null | undefined) {
  // Если рейтинг отсутствует или равен 0, используем темно-серый цвет
  if (gearScore == null || gearScore === 0 || !isFinite(gearScore)) {
    const darkGray = { r: 107, g: 114, b: 128 }; // #6b7280 в RGB
    const brighten = (value: number, factor: number) => Math.min(255, value + factor);
    const darken = (value: number, factor: number) => Math.max(0, value - factor);
    
    return {
      start: `rgb(${brighten(darkGray.r, 15)}, ${brighten(darkGray.g, 15)}, ${brighten(darkGray.b, 15)})`,
      middle: `rgb(${darkGray.r}, ${darkGray.g}, ${darkGray.b})`,
      end: `rgb(${darken(darkGray.r, 15)}, ${darken(darkGray.g, 15)}, ${darken(darkGray.b, 15)})`
    };
  }
  
  // Используем ту же цветовую шкалу, что и для стратегий
  const baseColor = getStrategyScoreColor(gearScore);
  
  // Парсим hex цвет
  let r, g, b;
  if (baseColor.startsWith('#')) {
    const hex = baseColor.slice(1);
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else {
    // Fallback
    return {
      start: baseColor,
      middle: baseColor,
      end: baseColor
    };
  }
  
  // Создаем градиент с ярким ободком
  const brighten = (value: number, factor: number) => Math.min(255, value + factor);
  const darken = (value: number, factor: number) => Math.max(0, value - factor);
  
  return {
    start: `rgb(${brighten(r, 60)}, ${brighten(g, 60)}, ${brighten(b, 60)})`,
    middle: `rgb(${brighten(r, 40)}, ${brighten(g, 40)}, ${brighten(b, 40)})`,
    end: `rgb(${brighten(r, 30)}, ${brighten(g, 30)}, ${brighten(b, 30)})`
  };
}
